import React, { useMemo } from 'react';
import { Box, Field, FieldTypes, Inscription } from '@common-fe/common-fe';
import * as yup from 'yup';

import { VALIDATORS } from '@/common';
import {
  INTEGER_VALUE_TEXT,
  INVALID_RANGE_TEXT,
  REQUIRED_TEXT,
} from '@/common/constants';
import { PaymentMethodType } from '@/modules/employee/employee.types';
import {
  ClaimOffsetLogic,
  OverridablePropertiesDto,
  WaitingForFundsContinuePayment,
} from '@/modules/employer/types';

import FraudThresholdHeader, {
  FRAUD_THRESHOLD_ANCHOR_CLASS,
} from '../../FraudThreshold/components/FraudThresholdHeader';
import useFraudThresholdFields from '../../FraudThreshold/hooks/useFraudThresholdFields';
import EmployeePaidFeeAmount from '../EmployeePaidFeeAmount';

import useGetReimbursementFieldsPermProps from './useGetReimbursementFieldsPermProps';

export const defaultClaimOffsetLogic = ClaimOffsetLogic.PAY_FROM_ANY_ACCOUNT;
export const WAITING_FOR_FUNDS_EXPIRATION_RANGE = [1, 999999];
const WAITING_FOR_FUNDS_EXPIRATION_VALIDATE = VALIDATORS.REQUIRED_STRING.test({
  test(val) {
    if (val && (+val < WAITING_FOR_FUNDS_EXPIRATION_RANGE[0] || +val > WAITING_FOR_FUNDS_EXPIRATION_RANGE[1])) return false;
    return true;
  },
  message: `Should be in the range of ${WAITING_FOR_FUNDS_EXPIRATION_RANGE[0]} to ${WAITING_FOR_FUNDS_EXPIRATION_RANGE[1]}`,
});

const currencyValueValidate = yup
  .string()
  .test({
    test: (val) => {
      if (val) {
        const value = Number(val);
        return value >= 1 && value <= 999999;
      }
      return true;
    },
    message: INVALID_RANGE_TEXT(1, 999999),
  })
  .test({
    test: (val) => {
      if (val) {
        const value = Number(val);
        return Number.isInteger(value);
      }
      return true;
    },
    message: INTEGER_VALUE_TEXT,
  });

export enum BoolOption {
  yes = 'yes',
  no = 'no',
}

const getBoolOption = (value?: boolean) => {
  if (value === true) return BoolOption.yes;
  if (value === false) return BoolOption.no;

  return undefined;
};

const useGetReimbursementFields = (
  areAllowedPaymentMethodsShown?: boolean,
  allowedPaymentMethods?: PaymentMethodType[],
  overridableProperties?: OverridablePropertiesDto,
  isLoading?: boolean,
  disabled?: boolean,
  isEditMode?: boolean,
  isClaimOffsetsAllowed?: boolean,
  isFraudThresholdAvailable?: boolean,
  isBillPayAllowedShown?: boolean,
  showFraudThresholdError?: boolean,
) => {
  const {
    minimumPaymentReimbursementAmountPermProps,
    waitingForFundsContinuePaymentPermProps,
    waitingForFundsExpirationPermProps,
    isReimbursementAllowedPermProps,
    isBillPayAllowedPermProps,
    isClaimOffsetAllowedPermProps,
    claimOffsetLogicPermProps,
    hasFields,
  } = useGetReimbursementFieldsPermProps();
  const hasPaymentMethods = useMemo(() => {
    if (isLoading) return false;

    return typeof areAllowedPaymentMethodsShown === 'boolean'
      ? areAllowedPaymentMethodsShown
      : overridableProperties?.isReimbursementAllowed;
  },
  [areAllowedPaymentMethodsShown, overridableProperties, isLoading]);
  const isBillPayAllowed = useMemo(() => (
    typeof isBillPayAllowedShown === 'boolean'
      ? isBillPayAllowedShown
      : overridableProperties?.isBillPayAllowed
  ), [isBillPayAllowedShown, overridableProperties]);
  const isFraudThresholdVisible = Boolean(
    isFraudThresholdAvailable && (hasPaymentMethods || isBillPayAllowed)
  );
  const fraudThresholdFields = useFraudThresholdFields(
    overridableProperties,
    disabled,
    showFraudThresholdError,
    !isEditMode,
    isFraudThresholdVisible
  );
  const fields = useMemo(
    () => {
      if (isLoading || !hasFields) return [];
      const isEmployeePaymentFeeDisabled = !isEditMode || disabled || isReimbursementAllowedPermProps?.disabled;

      return [
        {
          name: 'allowReimbursement',
          label: 'Allow Reimbursement',
          type: FieldTypes.Radio,
          showRequireIcon: true,
          disabled,
          validator: yup.string().required(REQUIRED_TEXT),
          value: getBoolOption(overridableProperties?.isReimbursementAllowed),
          options: [{
            label: 'Yes',
            value: BoolOption.yes,
          }, {
            label: 'No',
            value: BoolOption.no,
          }],
          ...isReimbursementAllowedPermProps,
        },
        ...(hasPaymentMethods ? [
          {
            name: 'allowedPaymentMethods',
            label: (
              <Box flex={{ grow: 1 }}>
                <Inscription margin={{ bottom: '4px' }} weight="bold" color="textBody">
                  Allowed payment methods
                </Inscription>
                <Box>
                  <Inscription weight="normal" color="textSecondary" size="12px" lineHeight="18px">
                    At least one option is required
                  </Inscription>
                </Box>
              </Box>
            ),
            type: FieldTypes.CheckBoxGroup,
            disabled,
            showRequireIcon: true,
            hasHorizontalLine: isFraudThresholdVisible,
            ...((!allowedPaymentMethods
              && !overridableProperties?.allowedPaymentMethods?.length)
              || allowedPaymentMethods ? {
                validator: yup.array().min(1, REQUIRED_TEXT).required(REQUIRED_TEXT),
              } : {}),
            value: overridableProperties?.allowedPaymentMethods,
            options: [{
              value: 'Direct Deposit',
              key: PaymentMethodType.DIRECT_DEPOSIT,
              ...(allowedPaymentMethods?.includes(PaymentMethodType.DIRECT_DEPOSIT) ? {
                node: (
                  <EmployeePaidFeeAmount
                    paymentMethodType={PaymentMethodType.DIRECT_DEPOSIT}
                    overridableProperties={overridableProperties}
                    disabled={isEmployeePaymentFeeDisabled}
                  />
                ),
              } : {}),
            }, {
              value: 'My Personal Debit Card',
              key: PaymentMethodType.DEBIT,
              ...(allowedPaymentMethods?.includes(PaymentMethodType.DEBIT) ? {
                node: (
                  <EmployeePaidFeeAmount
                    paymentMethodType={PaymentMethodType.DEBIT}
                    overridableProperties={overridableProperties}
                    disabled={isEmployeePaymentFeeDisabled}
                  />
                ),
              } : {}),
            }, {
              value: 'PayPal',
              key: PaymentMethodType.PAYPAL,
              ...(allowedPaymentMethods?.includes(PaymentMethodType.PAYPAL) ? {
                node: (
                  <EmployeePaidFeeAmount
                    paymentMethodType={PaymentMethodType.PAYPAL}
                    overridableProperties={overridableProperties}
                    disabled={isEmployeePaymentFeeDisabled}
                  />
                ),
              } : {}),
            }, {
              value: 'Venmo',
              key: PaymentMethodType.VENMO,
              ...(allowedPaymentMethods?.includes(PaymentMethodType.VENMO) ? {
                node: (
                  <EmployeePaidFeeAmount
                    paymentMethodType={PaymentMethodType.VENMO}
                    overridableProperties={overridableProperties}
                    disabled={isEmployeePaymentFeeDisabled}
                  />),
              } : {}),
            }, {
              value: 'Check',
              key: PaymentMethodType.CHECK,
              ...(allowedPaymentMethods?.includes(PaymentMethodType.CHECK) ? {
                node: (
                  <EmployeePaidFeeAmount
                    paymentMethodType={PaymentMethodType.CHECK}
                    overridableProperties={overridableProperties}
                    disabled={isEmployeePaymentFeeDisabled}
                  />
                ),
              } : {}),
            }],
            ...isReimbursementAllowedPermProps,
          },
        ] : []),
        {
          name: 'allowBillPay',
          label: 'Allow Bill Pay',
          type: FieldTypes.Radio,
          disabled,
          showRequireIcon: true,
          hasHorizontalLine: isFraudThresholdVisible,
          ...(isFraudThresholdVisible ? { fieldWrapperStyles: { marginBottom: 0 } } : {}),
          validator: yup.string().required(REQUIRED_TEXT),
          value: getBoolOption(overridableProperties?.isBillPayAllowed),
          options: [{
            label: 'Yes',
            value: BoolOption.yes,
          }, {
            label: 'No',
            value: BoolOption.no,
          }],
          ...isBillPayAllowedPermProps,
        },
        ...(isFraudThresholdVisible ? [
          {
            name: 'fraudThresholdHeader',
            type: FieldTypes.Node,
            fill: true,
            inputWrapCssClass: FRAUD_THRESHOLD_ANCHOR_CLASS,
            fieldWrapperStyles: { marginBottom: '16px' },
            value: <FraudThresholdHeader />,
          },
          ...fraudThresholdFields,
        ] : []),
        {
          name: 'allowClaimOffsets',
          label: 'Allow Claim Offsets',
          type: FieldTypes.Radio,
          disabled,
          showRequireIcon: true,
          validator: yup.string().required(REQUIRED_TEXT),
          value: getBoolOption(overridableProperties?.isClaimOffsetsAllowed),
          options: [{
            label: 'Yes',
            value: BoolOption.yes,
          }, {
            label: 'No',
            value: BoolOption.no,
          }],
          ...isClaimOffsetAllowedPermProps,
        },
        ...isClaimOffsetsAllowed ? [
          {
            name: 'claimOffsetLogic',
            label: 'Offset logic',
            type: FieldTypes.Radio,
            disabled,
            showRequireIcon: true,
            value: overridableProperties?.claimOffsetLogic || defaultClaimOffsetLogic,
            options: [{
              label: 'Pay from any account',
              value: ClaimOffsetLogic.PAY_FROM_ANY_ACCOUNT,
            }, {
              label: 'Pay from the same account',
              value: ClaimOffsetLogic.PAY_FROM_SAME_ACCOUNT,
            }],
            ...claimOffsetLogicPermProps,
          },
        ] : [],
        {
          name: 'minimumPaymentReimbursementAmount',
          type: FieldTypes.Currency,
          label: 'Waiting for Funds - Minimum Payment',
          showRequireIcon: true,
          placeholder: 'Enter $ amount',
          value: overridableProperties?.minimumPaymentReimbursementAmount,
          defaultValue: overridableProperties?.minimumPaymentReimbursementAmount,
          disabled,
          validator: currencyValueValidate,
          ...minimumPaymentReimbursementAmountPermProps,
        },
        {
          name: 'waitingForFundsContinuePayment',
          label: 'Waiting for Funds - Continue Payment',
          type: FieldTypes.Radio,
          disabled,
          value: overridableProperties?.waitingForFundsContinuePayment,
          options: [{
            label: 'Employee choice',
            value: WaitingForFundsContinuePayment.EMPLOYEE_CHOICE,
          }, {
            label: 'Autopay',
            value: WaitingForFundsContinuePayment.AUTOPAY,
          }],
          ...waitingForFundsContinuePaymentPermProps,
        },
        {
          name: 'waitingForFundsExpiration',
          label: (
            <Box flex={{ grow: 1 }}>
              <Inscription margin={{ bottom: '4px' }} weight="bold" color="textBody" lineHeight="20px">
                Days to process claims waiting for funds
              </Inscription>
              <Box>
                <Inscription weight={500} color="textSecondary" size="12px" lineHeight="18px">
                  The number of days the system will continue to look for funds that can pay claims in the 'waiting for funds' status
                </Inscription>
              </Box>
            </Box>
          ),
          showRequireIcon: true,
          validator: WAITING_FOR_FUNDS_EXPIRATION_VALIDATE,
          type: FieldTypes.Number,
          disabled,
          value: overridableProperties?.waitingForFundsExpiration,
          ...waitingForFundsExpirationPermProps,
        },
      ];
    },
    [
      isEditMode,
      hasFields,
      waitingForFundsExpirationPermProps,
      overridableProperties,
      hasPaymentMethods,
      allowedPaymentMethods,
      isLoading,
      disabled,
      waitingForFundsContinuePaymentPermProps,
      isReimbursementAllowedPermProps,
      isBillPayAllowedPermProps,
      minimumPaymentReimbursementAmountPermProps,
      isClaimOffsetAllowedPermProps,
      claimOffsetLogicPermProps,
      isClaimOffsetsAllowed,
      isFraudThresholdVisible,
      fraudThresholdFields,
    ],
  );

  return fields as Field[];
};

export default useGetReimbursementFields;
